import './App.css';
import { useEffect, useMemo, useRef, useState } from 'react';
import { saveDraft, loadDraft, listDrafts, deleteDraft, DraftConflictError } from './services/draftService';

// Hooks
import { useAuthState } from './hooks/useAuthState';
import { useDraftState } from './hooks/useDraftState';
import { useDraftSync } from './hooks/useDraftSync';
import { usePlayerData, sanitizePlayerData } from './hooks/usePlayerData';
import { useUIState } from './hooks/useUIState';

// Utils
import {
  getPositionDisplay,
  getPlayerProjected,
  getFormattedTeamAndPosition,
  getAvailablePlayers,
  getBestAvailableForPosition,
  getAllPlayers
} from './utils/playerHelpers';
import {
  getCurrentTeam,
  isTeamsTurn,
  getPicksUntilNextTurn as calcPicksUntilNextTurn,
  getRemainingCounts,
  debounce,
  buildDraftState,
  reconstructDraft
} from './utils/draftHelpers';
import {
  calculateTeamProjected,
  calculateTeamFinal,
  findPlayerFinalPoints,
  getRecommendedPlayerMonteCarlo,
  draftStateSeed
} from './utils/draftCalculations';

// Components
import AuthForm from './components/AuthForm/AuthForm';
import Home from './components/Home/Home';
import DraftConfig from './components/DraftConfig/DraftConfig';
import LoadDraftView from './components/LoadDraftView/LoadDraftView';
import DraftView from './components/DraftView/DraftView';
import ScoreView from './components/ScoreView/ScoreView';

// Header sync circle: green when synced, yellow while syncing/saving, red otherwise.
const SYNC_DOT = {
  waiting: {
    className: 'not-synced',
    title: 'No draft version from the server yet. If this persists, the backend API is outdated and needs to be redeployed.'
  },
  paused: {
    className: 'paused',
    title: 'Sync paused. Remote changes are not applied until you resume or sync manually.'
  },
  checking: { className: 'syncing', title: "Sync'ing..." },
  synced: { className: 'synced', title: 'In sync' },
  error: {
    className: 'not-synced',
    title: 'Could not check for draft updates; see the browser console.'
  }
};

function App() {
  // Custom hooks
  const auth = useAuthState();
  const draft = useDraftState();
  const playerData = usePlayerData(auth.username);
  const ui = useUIState();
  const [showScoreView, setShowScoreView] = useState(false);
  // Final actual points per player, once a final points file has been provided
  // through the Score dialog. Scores render alongside projections until the
  // draft context changes.
  const [finalPoints, setFinalPoints] = useState(null);

  // Keep the saved player data file list fresh while the config page is open.
  const refreshSavedPlayerFiles = playerData.refreshSavedFiles;
  useEffect(() => {
    if (auth.isAuthenticated && draft.view === 'config') {
      refreshSavedPlayerFiles();
    }
  }, [auth.isAuthenticated, draft.view, refreshSavedPlayerFiles]);

  // Player data reference
  const playersByPosition = playerData.customPlayerData || playerData.availablePlayers;

  // Cross-browser sync bookkeeping. draftEtagRef is the server version this
  // browser last saved or applied. changeSeqRef is 0 when every local change
  // has been saved (nonzero while a debounced save is pending or in flight).
  // skipNextAutoSaveRef suppresses the auto-save that applying a remote update
  // would otherwise trigger.
  const draftEtagRef = useRef(null);
  const changeSeqRef = useRef(0);
  const skipNextAutoSaveRef = useRef(false);

  const applyRemoteDraft = (remoteDraft, remoteEtag) => {
    const restored = reconstructDraft(remoteDraft);
    if (!restored.valid) return;

    skipNextAutoSaveRef.current = true;
    draftEtagRef.current = remoteEtag;
    playerData.setCustomPlayerData(
      restored.customPlayerData && sanitizePlayerData(restored.customPlayerData)
    );
    playerData.setAvailablePlayers(sanitizePlayerData(restored.availablePlayers || {}));
    draft.setConfig(restored.config);
    draft.setDraftedPlayers(restored.draftedPlayers);
    draft.setSelectedPlayers(restored.selectedPlayers);
    draft.setDraftHistory(restored.draftHistory);
    draft.setRedoStack(restored.redoStack);
  };

  // A polled remote change is applied only when everything local is saved;
  // otherwise our pending save hits a 409 and resyncAfterConflict handles it.
  const handleRemoteDraftChange = (remoteDraft, remoteEtag) => {
    if (changeSeqRef.current !== 0) {
      console.info('Remote draft update received while local changes are unsaved; deferring.');
      return;
    }
    console.info('Applying remote draft update', remoteEtag);
    applyRemoteDraft(remoteDraft, remoteEtag);
  };

  const { syncStatus, isSyncPaused, pauseSync, resumeSync, syncNow } = useDraftSync({
    enabled: auth.isAuthenticated && draft.view === 'draft' && Boolean(draft.currentDraftName),
    username: auth.username,
    draftName: draft.currentDraftName,
    etagRef: draftEtagRef,
    onRemoteChange: handleRemoteDraftChange
  });

  // Latest values for the auto-saver, read at fire time to avoid stale closures.
  const autoSaveContext = useRef({});

  // One stable debounced saver so rapid picks coalesce into a single save that
  // fires 2s after the last change (rather than one timer per change).
  const debouncedAutoSave = useMemo(
    () => debounce(async (state, seq) => {
      const { username, draftName, setIsAutoSaving, resyncAfterConflict } = autoSaveContext.current;
      if (!draftName) return;

      try {
        setIsAutoSaving(true);
        const baseEtag = draftEtagRef.current;
        const response = await saveDraft(username, draftName, state, baseEtag);
        if (!response.etag) {
          console.warn('Save succeeded but the server returned no version; cross-browser sync is disabled. Deploy the updated backend (cdk deploy).');
        }
        console.info(`Auto-save complete: version ${baseEtag} -> ${response.etag}`);
        draftEtagRef.current = response.etag;
        // Only mark clean if no newer local change arrived while saving.
        if (changeSeqRef.current === seq) {
          changeSeqRef.current = 0;
        }
      } catch (error) {
        if (error instanceof DraftConflictError) {
          await resyncAfterConflict();
        } else {
          console.error(`Error during auto-save (draft "${draftName}", base version ${draftEtagRef.current}):`, error);
          // An unsaved change also blocks applying remote updates, so a save
          // that fails and is never retried would freeze sync in this browser.
          // Retry unless a newer change has its own save queued.
          if (changeSeqRef.current === seq) {
            debouncedAutoSave(state, seq);
          }
        }
      } finally {
        setIsAutoSaving(false);
      }
    }, 2000),
    []
  );

  // The other browser saved first: drop the losing local change, take the
  // server version, and tell the user to re-enter whatever got dropped.
  const resyncAfterConflict = async () => {
    try {
      const { username, draftName } = autoSaveContext.current;
      const response = await loadDraft(username, draftName);
      debouncedAutoSave.cancel();
      changeSeqRef.current = 0;
      applyRemoteDraft(response.draft_data, response.etag);
      alert('This draft was updated in another browser, so the latest version was loaded. Please re-enter your last change if it is missing.');
    } catch (error) {
      console.error('Error resyncing draft after save conflict:', error);
    }
  };

  autoSaveContext.current = {
    username: auth.username,
    draftName: draft.currentDraftName,
    setIsAutoSaving: draft.setIsAutoSaving,
    resyncAfterConflict
  };

  // Auto-save effect
  useEffect(() => {
    if (!draft.currentDraftName) return;

    if (skipNextAutoSaveRef.current) {
      skipNextAutoSaveRef.current = false;
      return;
    }

    // Never persist a state with no player data: such a record loses every
    // projection and cannot be reconstructed on load.
    const hasPlayerData = playerData.customPlayerData ||
      Object.values(playerData.availablePlayers).some(players => players.length > 0);
    if (!hasPlayerData) return;

    const draftState = buildDraftState({
      config: draft.config,
      draftedPlayers: draft.draftedPlayers,
      selectedPlayers: draft.selectedPlayers,
      draftHistory: draft.draftHistory,
      redoStack: draft.redoStack,
      customPlayerData: playerData.customPlayerData,
      availablePlayers: playerData.availablePlayers
    });

    changeSeqRef.current += 1;
    debouncedAutoSave(draftState, changeSeqRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.draftedPlayers, draft.selectedPlayers, draft.draftHistory, draft.redoStack, playerData.availablePlayers]);

  // Handle click outside autocomplete
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ui.autocompleteRef.current && !ui.autocompleteRef.current.contains(event.target)) {
        ui.setAutocomplete({
          isOpen: false,
          fieldId: null,
          searchText: '',
          position: null,
          suggestions: []
        });
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update recommendations when draft state changes. Uses the deep Monte Carlo
  // simulation (maximizes expected total roster points) as the live recommender.
  // The compute is deferred until after paint so the drafted pick shows instantly
  // and the (heavier) recommendation fills in a moment later, with a computing
  // indicator in the meantime. A rapid follow-up pick cancels the pending run.
  // While Full AutoDraft is on, each computed recommendation is drafted right
  // away, which changes the board and schedules the next pick, until no player
  // can be recommended (every slot filled) or a pick cannot be placed.
  useEffect(() => {
    ui.setIsRecommending(true);
    const handle = setTimeout(() => {
      // Never recommend a player who is already on a roster, even if a stale
      // or legacy availablePlayers pool (e.g. from a restored draft) still
      // lists them.
      const draftedNames = new Set(Object.values(draft.draftedPlayers));
      const undraftedPool = {};
      Object.entries(playerData.availablePlayers).forEach(([position, players]) => {
        undraftedPool[position] = players.filter(p => !draftedNames.has(p.name));
      });
      // Seed the simulations from the board state and raise the simulation
      // count so the recommendation is reproducible and near-tied candidates
      // resolve consistently instead of flickering between recomputes.
      const recommendation = getRecommendedPlayerMonteCarlo(
        draft.draftedPlayers,
        draft.config,
        undraftedPool,
        { seed: draftStateSeed(draft.draftedPlayers), simulations: 400 }
      );
      ui.setRecommendedPlayer(recommendation.player);
      ui.setRecommendedEV(recommendation.ev);
      ui.setPicksUntilNextTurn(recommendation.picksUntilNext);
      ui.setFutureProjected(recommendation.futureProjected);
      ui.setPositionEVs(recommendation.positionEVs);
      ui.setIsRecommending(false);

      if (ui.isFullAutoDrafting) {
        const drafted = recommendation.player && handleQuickDraft(recommendation.player);
        if (!drafted) ui.setIsFullAutoDrafting(false);
      }
    }, 0);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.draftedPlayers, draft.selectedPlayers, ui.isFullAutoDrafting]);

  // Draft handlers
  const handleStartDraft = async () => {
    if (!playerData.customPlayerData) {
      alert('Please upload player data before starting a draft');
      return;
    }

    try {
      const response = await listDrafts(auth.username);
      const existingDrafts = response.drafts;

      if (existingDrafts.some(d => d.draft_name === draft.draftName)) {
        alert('A draft with this name already exists. Please choose a different name.');
        return;
      }

      draft.setDraftedPlayers({});
      draft.setSelectedPlayers(new Set());
      ui.setDraftPosition(0);
      ui.setQuickSearch('');
      ui.setQuickSearchResults([]);
      ui.setSearchTerm('');
      ui.setAutocomplete({
        isOpen: false,
        fieldId: null,
        searchText: '',
        position: null,
        suggestions: []
      });

      draft.setCurrentDraftName(draft.draftName);
      // New draft: no server version yet, first save creates the object.
      draftEtagRef.current = null;
      changeSeqRef.current = 0;
      setFinalPoints(null);

      playerData.setAvailablePlayers({
        QB: [...playerData.customPlayerData.QB],
        RB: [...playerData.customPlayerData.RB],
        WR: [...playerData.customPlayerData.WR],
        TE: [...playerData.customPlayerData.TE],
        K: [...playerData.customPlayerData.K],
        DEF: [...playerData.customPlayerData.DEF]
      });

      draft.setView('draft');
    } catch (error) {
      console.error('Error checking existing drafts:', error);
      alert('Error checking existing drafts. Please try again.');
    }
  };

  // Restore a player to every position list they are eligible in.
  const restorePlayerToAvailable = (playerName) => {
    playerData.setAvailablePlayers(prev => {
      const updated = { ...prev };
      for (const pos of Object.keys(playersByPosition)) {
        const player = playersByPosition[pos].find(p => p.name === playerName);
        if (player && !updated[pos].some(p => p.name === playerName)) {
          updated[pos] = [...updated[pos], player].sort((a, b) => b.projected - a.projected);
        }
      }
      return updated;
    });
  };

  const handleDraftAction = (fieldId, playerName, isUndo = false) => {
    if (!isUndo) {
      draft.setDraftHistory(prev => [...prev, {
        draftedPlayers: draft.draftedPlayers,
        selectedPlayers: new Set(draft.selectedPlayers),
        availablePlayers: { ...playerData.availablePlayers }
      }]);
      draft.setRedoStack([]);
    }

    const existingPlayer = draft.draftedPlayers[fieldId];
    if (existingPlayer) {
      draft.setSelectedPlayers(prev => {
        const newSelected = new Set(prev);
        newSelected.delete(existingPlayer);
        return newSelected;
      });

      restorePlayerToAvailable(existingPlayer);
    }

    draft.setDraftedPlayers(prev => ({
      ...prev,
      [fieldId]: playerName
    }));

    draft.setSelectedPlayers(prev => new Set([...prev, playerName]));

    // A player can appear in multiple lists (e.g. TE also listed as WR);
    // drafting removes them from every list they are eligible in.
    playerData.setAvailablePlayers(prev => {
      const updated = { ...prev };
      for (const pos of Object.keys(updated)) {
        if (updated[pos].some(p => p.name === playerName)) {
          updated[pos] = updated[pos].filter(p => p.name !== playerName);
        }
      }
      return updated;
    });
  };

  const handleUndo = () => {
    if (draft.draftHistory.length === 0) return;

    const lastState = draft.draftHistory[draft.draftHistory.length - 1];

    draft.setRedoStack(prev => [...prev, {
      draftedPlayers: draft.draftedPlayers,
      selectedPlayers: new Set(draft.selectedPlayers),
      availablePlayers: { ...playerData.availablePlayers }
    }]);

    draft.setDraftedPlayers(lastState.draftedPlayers);
    draft.setSelectedPlayers(lastState.selectedPlayers);
    playerData.setAvailablePlayers(lastState.availablePlayers);

    draft.setDraftHistory(prev => prev.slice(0, -1));
  };

  const handleRedo = () => {
    if (draft.redoStack.length === 0) return;

    const nextState = draft.redoStack[draft.redoStack.length - 1];

    draft.setDraftHistory(prev => [...prev, {
      draftedPlayers: draft.draftedPlayers,
      selectedPlayers: new Set(draft.selectedPlayers),
      availablePlayers: { ...playerData.availablePlayers }
    }]);

    draft.setDraftedPlayers(nextState.draftedPlayers);
    draft.setSelectedPlayers(nextState.selectedPlayers);
    playerData.setAvailablePlayers(nextState.availablePlayers);

    draft.setRedoStack(prev => prev.slice(0, -1));
  };

  const handlePlayerSelect = (player) => {
    const fieldId = ui.popup.fieldId;
    handleDraftAction(fieldId, player.name);
    ui.setPopup({ ...ui.popup, isOpen: false });
  };

  const handlePositionClick = (position, teamIndex, positionIndex) => {
    ui.setPopup({
      isOpen: true,
      position,
      teamIndex,
      positionIndex,
      fieldId: `${teamIndex}-${position}-${positionIndex}`
    });
  };

  const handleRemovePlayer = (fieldId, playerName) => {
    draft.setDraftedPlayers(prev => {
      const newDrafted = { ...prev };
      delete newDrafted[fieldId];
      return newDrafted;
    });

    draft.setSelectedPlayers(prev => {
      const newSelected = new Set(prev);
      newSelected.delete(playerName);
      return newSelected;
    });

    restorePlayerToAvailable(playerName);
  };

  const handleFieldInput = (fieldId, position, value) => {
    if (!value || !value.trim()) {
      ui.setAutocomplete({
        isOpen: false,
        fieldId: null,
        searchText: '',
        position: null,
        suggestions: []
      });
      return;
    }

    const suggestions = getAvailablePlayers(position, playerData.availablePlayers, value)
      .filter(player =>
        player.name &&
        player.name.toLowerCase().includes(value.toLowerCase()) &&
        !draft.selectedPlayers.has(player.name)
      )
      .slice(0, 15);

    ui.setAutocomplete({
      isOpen: suggestions.length > 0,
      fieldId,
      searchText: value,
      position,
      suggestions
    });
  };

  const handleAutoCompleteSelect = (player) => {
    handleDraftAction(ui.autocomplete.fieldId, player.name);
    ui.setAutocomplete({
      isOpen: false,
      fieldId: null,
      searchText: '',
      position: null,
      suggestions: []
    });
  };

  const handleQuickSearch = (value) => {
    ui.setQuickSearch(value);
    if (!value.trim()) {
      ui.setQuickSearchResults([]);
      return;
    }

    const allPlayers = [
      ...playersByPosition.QB,
      ...playersByPosition.RB,
      ...playersByPosition.WR,
      ...playersByPosition.TE,
      ...playersByPosition.K,
      ...playersByPosition.DEF
    ].filter(p => !draft.selectedPlayers.has(p.name));

    const results = allPlayers
      .filter(player =>
        player.name.toLowerCase().includes(value.toLowerCase())
      )
      .slice(0, 10);

    ui.setQuickSearchResults(results);
  };

  const handleQuickDraft = (player) => {
    const totalPicks = Object.values(draft.draftedPlayers).length;
    const roundNumber = Math.floor(totalPicks / draft.config.teamCount);
    const pickInRound = totalPicks % draft.config.teamCount;
    const draftingTeam = roundNumber % 2 === 0
      ? pickInRound
      : (draft.config.teamCount - 1 - pickInRound);

    const playerPosition = getPositionDisplay(player, 'BENCH', playersByPosition);
    // TE-eligible players can also appear in the WR list; prefer an open TE slot.
    const isAlsoTE = playerPosition === 'WR' &&
      (playersByPosition.TE || []).some(p => p.name === player.name);

    let positions = Object.entries(draft.config.positions);
    if (isAlsoTE) {
      positions = [
        ...positions.filter(([pos]) => pos === 'TE'),
        ...positions.filter(([pos]) => pos !== 'TE')
      ];
    }
    let fieldId = null;

    for (const [pos, count] of positions) {
      if (count === 0) continue;

      const isValidPosition = (pos === 'BENCH') ||
        (isAlsoTE && pos === 'TE') ||
        (pos === 'FLEX' && ['RB', 'WR', 'TE'].includes(playerPosition)) ||
        (pos === 'SFLEX' && ['QB', 'RB', 'WR', 'TE'].includes(playerPosition)) ||
        (playerPosition === pos);

      if (!isValidPosition) continue;

      for (let i = 0; i < count; i++) {
        const currentFieldId = `${draftingTeam}-${pos}-${i}`;
        if (!draft.draftedPlayers[currentFieldId]) {
          fieldId = currentFieldId;
          break;
        }
      }

      if (fieldId) break;
    }

    if (fieldId) {
      handleDraftAction(fieldId, player.name);
      return true;
    } else {
      const teamSelections = Object.fromEntries(
        Object.entries(draft.draftedPlayers).filter(([id]) => id.startsWith(`${draftingTeam}-`))
      );
      console.error(
        `Quick draft failed: no open slot for ${player.name} (${playerPosition || 'unknown position'}${isAlsoTE ? ', also TE' : ''}) on team ${draftingTeam}` +
        `${draft.config.teamNames[draftingTeam] ? ` (${draft.config.teamNames[draftingTeam]})` : ''}. Current selections:`,
        teamSelections
      );
      alert('No valid position available for this player on the drafting team');
      return false;
    }
  };

  // Draft the currently displayed recommendation. Using the shown player (rather
  // than recomputing) keeps auto-draft consistent with the Monte Carlo result on
  // screen, which is otherwise nondeterministic across runs.
  const handleAutoDraft = () => {
    if (ui.recommendedPlayer) {
      handleQuickDraft(ui.recommendedPlayer);
    }
  };

  // Toggle: pressing while a run is in progress stops it after the current pick.
  const handleFullAutoDraft = () => {
    ui.setIsFullAutoDrafting(prev => !prev);
  };

  const handleSaveDraft = async () => {
    try {
      if (!draft.currentDraftName) {
        alert('No draft name available');
        return;
      }

      const draftState = buildDraftState({
        config: draft.config,
        draftedPlayers: draft.draftedPlayers,
        selectedPlayers: draft.selectedPlayers,
        draftHistory: draft.draftHistory,
        redoStack: draft.redoStack,
        customPlayerData: playerData.customPlayerData,
        availablePlayers: playerData.availablePlayers
      });

      const saveResponse = await saveDraft(auth.username, draft.currentDraftName, draftState, draftEtagRef.current);
      draftEtagRef.current = saveResponse.etag;
      debouncedAutoSave.cancel();
      changeSeqRef.current = 0;
      alert('Draft saved successfully!');

      const response = await listDrafts(auth.username);
      draft.setSavedDrafts(response.drafts);
    } catch (error) {
      if (error instanceof DraftConflictError) {
        await resyncAfterConflict();
        return;
      }
      console.error('Error saving draft:', error);
      alert('Failed to save draft. Please try again.');
    }
  };

  const loadSavedDrafts = async () => {
    draft.setIsLoadingDrafts(true);
    try {
      const drafts = await listDrafts(auth.username);
      draft.setSavedDrafts(drafts.drafts);
      draft.setLoadDraftView(true);
    } catch (error) {
      console.error('Error loading drafts:', error);
      alert('Failed to load drafts. Please try again.');
    } finally {
      draft.setIsLoadingDrafts(false);
    }
  };

  const handleLoadDraft = async (draftToLoad) => {
    try {
      draft.setIsLoadingDraft(true);
      draft.setCurrentLoadingDraft(draftToLoad.draft_name);

      const response = await loadDraft(auth.username, draftToLoad.draft_name);
      const loadedDraft = response.draft_data;

      const restored = reconstructDraft(loadedDraft);
      if (!restored.valid) {
        alert('No player data found in draft file');
        return;
      }

      // Loading is not a local change: don't let the state updates below
      // trigger an auto-save that would bump the version for the other browser.
      skipNextAutoSaveRef.current = true;

      playerData.setCustomPlayerData(restored.customPlayerData);
      playerData.setAvailablePlayers(restored.availablePlayers);

      draft.setConfig(restored.config);
      draft.setDraftedPlayers(restored.draftedPlayers);
      draft.setSelectedPlayers(restored.selectedPlayers);
      draft.setDraftHistory(restored.draftHistory);
      draft.setRedoStack(restored.redoStack);

      draft.setCurrentDraftName(draftToLoad.draft_name);
      draftEtagRef.current = response.etag;
      changeSeqRef.current = 0;
      setFinalPoints(null);
      draft.setLoadDraftView(false);
      draft.setView('draft');
    } catch (error) {
      console.error('Error loading draft:', error);
      alert('Failed to load draft. Please try again.');
    } finally {
      draft.setIsLoadingDraft(false);
      draft.setCurrentLoadingDraft(null);
    }
  };

  const handleDeleteDraft = async (draftToDelete, event) => {
    event.stopPropagation();

    if (!window.confirm(`Are you sure you want to delete "${draftToDelete.draft_name}"?`)) {
      return;
    }

    try {
      await deleteDraft(auth.username, draftToDelete.draft_name);
      draft.setSavedDrafts(drafts => drafts.filter(d => d.draft_name !== draftToDelete.draft_name));
      alert('Draft deleted successfully!');
    } catch (error) {
      console.error('Error deleting draft:', error);
      alert('Failed to delete draft. Please try again.');
    }
  };

  const handleTeamNameEdit = (teamIndex, newName) => {
    const newTeamNames = [...draft.config.teamNames];
    newTeamNames[teamIndex] = newName;
    draft.setConfig(prev => ({
      ...prev,
      teamNames: newTeamNames
    }));
  };

  const handleStartPresetDraft = (preset) => {
    handleViewChange('config');
    draft.setConfig({
      teamCount: preset.teamCount,
      positions: {
        QB: 0,
        RB: 0,
        WR: 0,
        TE: 0,
        FLEX: 0,
        SFLEX: 0,
        DEF: 0,
        K: 0,
        BENCH: 0,
        ...preset.positions
      },
      teamNames: []
    });
  };

  const handleViewChange = (newView) => {
    setShowScoreView(false);
    setFinalPoints(null);
    if (newView === 'config') {
      // Leaving a draft ends its save session before the player pool is reset:
      // otherwise the reset triggers an auto-save with no player data, which
      // permanently wipes the saved draft's projections.
      debouncedAutoSave.cancel();
      draft.setCurrentDraftName(null);
      draftEtagRef.current = null;
      changeSeqRef.current = 0;

      playerData.resetPlayerData();

      const today = new Date();
      const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      draft.setDraftName(`${months[today.getMonth()]} ${today.getDate()}, ${today.getFullYear()}`);
    }
    draft.setView(newView);
  };

  const handleLogout = async () => {
    await auth.handleLogout(() => {
      draft.resetDraftState();
      playerData.resetPlayerData();
    });
  };

  return (
    <div className="App">
      {!auth.isAuthenticated ? (
        <>
          <div className="app-header-fixed">
            <h1>Useful Chaos' Fantasy Drafter</h1>
          </div>
          <header className="App-header">
            <AuthForm
              authState={auth.authState}
              email={auth.email}
              password={auth.password}
              confirmationCode={auth.confirmationCode}
              authError={auth.authError}
              onEmailChange={auth.setEmail}
              onPasswordChange={auth.setPassword}
              onConfirmationCodeChange={auth.setConfirmationCode}
              onSignIn={auth.handleSignIn}
              onSignUp={auth.handleSignUp}
              onConfirmSignUp={auth.handleConfirmSignUp}
              onSwitchAuthState={auth.setAuthState}
            />
          </header>
        </>
      ) : (
        <>
          <div className="app-header-fixed">
            <div className="center-section">
              {draft.view === 'draft' && (
                <button className="header-button back" onClick={() => handleViewChange('config')}>
                  Back
                </button>
              )}

              {draft.view === 'config' && (
                <button className="header-button cancel" onClick={() => handleViewChange('initial')}>
                  Cancel
                </button>
              )}

              <div className="header-title">
                <h1>
                  Useful Chaos' Fantasy Drafter
                  {draft.view === 'initial' && !draft.loadDraftView ? ' - Home' : ''}
                  {draft.view === 'initial' && draft.loadDraftView ? ' - Load' : ''}
                  {draft.view === 'config' ? ' - Configuration' : ''}
                </h1>
              </div>

              {draft.view === 'draft' && (
                <>
                  <button className="header-button save" onClick={handleSaveDraft}>
                    {draft.isAutoSaving ? 'Saving...' : (draft.currentDraftName ? `Save ${draft.currentDraftName}` : 'Save Draft')}
                  </button>
                  {syncStatus !== 'off' && (
                    <>
                      <span
                        className={`header-sync-dot ${SYNC_DOT[draft.isAutoSaving ? 'checking' : syncStatus].className}`}
                        title={SYNC_DOT[draft.isAutoSaving ? 'checking' : syncStatus].title}
                      />
                      <div className="sync-controls">
                        <button className="sync-control-button" onClick={syncNow} title="Sync now">
                          ↻
                        </button>
                        {isSyncPaused ? (
                          <button className="sync-control-button resume" onClick={resumeSync} title="Resume sync">
                            ▶
                          </button>
                        ) : (
                          <button className="sync-control-button stop" onClick={pauseSync} title="Pause sync">
                            ■
                          </button>
                        )}
                      </div>
                    </>
                  )}
                  <button className="header-button score" onClick={() => setShowScoreView(true)}>
                    Score
                  </button>
                </>
              )}

              {draft.view === 'config' && (
                <button className="header-button start" onClick={handleStartDraft}>
                  Start
                </button>
              )}
            </div>

            <div className="user-section">
              <span className="username-display">({auth.username})</span>
              <button
                className="header-button logout"
                onClick={handleLogout}
              >
                Logout
              </button>
            </div>
          </div>

          <header className="App-header">
            {draft.view === 'initial' && !draft.loadDraftView && (
              <Home
                onStartNewDraft={() => handleViewChange('config')}
                onStartPresetDraft={handleStartPresetDraft}
                onLoadDraft={loadSavedDrafts}
                isAuthenticated={auth.isAuthenticated}
                onLogout={handleLogout}
                username={auth.username}
              />
            )}

            {draft.view === 'initial' && draft.loadDraftView && (
              <LoadDraftView
                savedDrafts={draft.savedDrafts}
                isLoadingDraft={draft.isLoadingDraft}
                currentLoadingDraft={draft.currentLoadingDraft}
                onLoadDraft={handleLoadDraft}
                onDeleteDraft={handleDeleteDraft}
                onBack={() => draft.setLoadDraftView(false)}
              />
            )}

            {draft.view === 'config' && (
              <DraftConfig
                config={draft.config}
                onConfigChange={draft.setConfig}
                onStartDraft={handleStartDraft}
                onViewChange={handleViewChange}
                customPlayerData={playerData.customPlayerData}
                onFileUpload={playerData.handleFileUpload}
                savedPlayerFiles={playerData.savedFiles}
                selectedFileName={playerData.selectedFileName}
                loadingFileName={playerData.loadingFileName}
                onSelectFile={playerData.selectSavedFile}
                onDeleteFile={playerData.deleteSavedFile}
                onRenameFile={playerData.renameSavedFile}
                draftName={draft.draftName}
                onDraftNameChange={draft.setDraftName}
              />
            )}

            {draft.view === 'draft' && (
              <DraftView
                config={draft.config}
                draftedPlayers={draft.draftedPlayers}
                popup={ui.popup}
                setPopup={ui.setPopup}
                autocomplete={ui.autocomplete}
                autocompleteRef={ui.autocompleteRef}
                editingTeamName={draft.editingTeamName}
                setEditingTeamName={draft.setEditingTeamName}
                quickSearch={ui.quickSearch}
                quickSearchResults={ui.quickSearchResults}
                recommendedPlayer={ui.recommendedPlayer}
                recommendedEV={ui.recommendedEV}
                isRecommending={ui.isRecommending}
                isRecommendationExpanded={ui.isRecommendationExpanded}
                setIsRecommendationExpanded={ui.setIsRecommendationExpanded}
                isFullAutoDrafting={ui.isFullAutoDrafting}
                positionEVs={ui.positionEVs}
                futureProjected={ui.futureProjected}
                showPlayersView={ui.showPlayersView}
                setShowPlayersView={ui.setShowPlayersView}
                draftHistory={draft.draftHistory}
                redoStack={draft.redoStack}
                availablePlayers={playerData.availablePlayers}
                selectedPlayers={draft.selectedPlayers}
                playersByPosition={playersByPosition}
                handleQuickSearch={handleQuickSearch}
                handleQuickDraft={handleQuickDraft}
                handlePositionClick={handlePositionClick}
                handlePlayerSelect={handlePlayerSelect}
                handleUndo={handleUndo}
                handleRedo={handleRedo}
                handleAutoDraft={handleAutoDraft}
                handleFullAutoDraft={handleFullAutoDraft}
                handleSaveDraft={handleSaveDraft}
                handleViewChange={handleViewChange}
                handleTeamNameEdit={handleTeamNameEdit}
                handleFieldInput={handleFieldInput}
                handleAutoCompleteSelect={handleAutoCompleteSelect}
                handleRemovePlayer={handleRemovePlayer}
                getRemainingCounts={() => getRemainingCounts(draft.config, draft.draftedPlayers)}
                calculateTeamProjected={(teamIndex) => calculateTeamProjected(teamIndex, draft.draftedPlayers, playersByPosition)}
                calculateTeamFinal={(teamIndex) => finalPoints ? calculateTeamFinal(teamIndex, draft.draftedPlayers, playersByPosition, finalPoints) : null}
                getPlayerFinalPoints={(playerName) => finalPoints ? findPlayerFinalPoints(playerName, playersByPosition, finalPoints) : null}
                isTeamsTurn={(teamIndex) => isTeamsTurn(teamIndex, draft.draftedPlayers, draft.config.teamCount)}
                getCurrentTeam={() => getCurrentTeam(draft.draftedPlayers, draft.config.teamCount)}
                getPicksUntilNextTurn={() => calcPicksUntilNextTurn(draft.draftedPlayers, draft.config.teamCount)}
                getPositionDisplay={(player, slotPosition) => getPositionDisplay(player, slotPosition, playersByPosition)}
                getBestAvailableForPosition={(pos) => getBestAvailableForPosition(pos, playersByPosition, draft.selectedPlayers)}
                getFormattedTeamAndPosition={(playerName, slotPosition) => getFormattedTeamAndPosition(playerName, slotPosition, playersByPosition)}
                getPlayerProjected={(playerName) => getPlayerProjected(playerName, playersByPosition)}
                getAllPlayers={() => getAllPlayers(playerData.customPlayerData, playerData.availablePlayers)}
                isActiveDraft={() => draft.currentDraftName !== null}
                isAutoSaving={draft.isAutoSaving}
                currentDraftName={draft.currentDraftName}
              />
            )}

            {draft.view === 'draft' && showScoreView && (
              <ScoreView
                username={auth.username}
                config={draft.config}
                draftedPlayers={draft.draftedPlayers}
                playersByPosition={playersByPosition}
                onFinalPointsLoaded={setFinalPoints}
                onClose={() => setShowScoreView(false)}
              />
            )}
          </header>
          <div className="footer">
            © 2025 - <a href="https://www.usefulchaos.com" target="_blank" rel="noopener noreferrer">UsefulChaos.com</a>
          </div>
        </>
      )}
    </div>
  );
}

export default App;
